import Vue, { h, defineComponent, ref, watch, VNodeChildren } from 'vue';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { renderAnalyticsTeaserPage } from '@/ui/pages/teasers/AnalyticsTeaserPage';
import { renderForbiddenPage } from '@/ui/pages/error/ForbiddenPage';
import { box, container, level, loader, namedLink, pageTitle } from '@/ui/components';
import { IdProps } from '@/ui/helpers/uiHelper';
import { useTask } from '@/ui/concerns';
import { checklistDetail, fetchQuestion } from '@/services/api/resources/analytics';
import Checklist from '@/_legacy/views/Analytics/Checklist/Checklist.vue';
import { t } from '@/ui/i18n/i18n';
import { BackButton } from '@/ui/components/button/BackButton';

type questionsResponse = Array<{ uuid: string; photos: Array<{ inspection_question_uuid: string }> }>;

export const SummaryChecklistsDetailPage = defineComponent<IdProps>({
  name: 'SummaryChecklistsDetailPage',
  props: ['id'],
  setup(props) {
    if (!hasFeature('analytics')) {
      return renderAnalyticsTeaserPage;
    }

    if (!hasPermission(Permissions.VIEW_ALL_ANALYTICS)) {
      return renderForbiddenPage;
    }

    const questionsUuids = ref<string[]>([]);

    // create empty record
    const record = ref<unknown>();

    let currentQuestionUuidBeingFetched: string;

    const fetchQuestionsOneByOne = async () => {
      if (questionsUuids.value.length < 1) {
        return;
      }

      currentQuestionUuidBeingFetched = questionsUuids.value.shift() as string;
      // @ts-ignore
      const { right: response } = await fetchQuestion(props.id.toString(), currentQuestionUuidBeingFetched)();

      const currentQuestions = (response.parsed as { data: unknown }).data as questionsResponse;

      const currentQuestionsWithUuid = currentQuestions.map(question => {
        question.uuid = currentQuestionUuidBeingFetched;
        question.photos = question.photos.map(questionPhoto => {
          questionPhoto.inspection_question_uuid = currentQuestionUuidBeingFetched;
          return questionPhoto;
        });
        return question;
      });

      Vue.set(
        (record.value as { questions: Record<string, Record<string, unknown>> }).questions,
        currentQuestionUuidBeingFetched,
        { questions: currentQuestionsWithUuid },
      );

      await fetchQuestionsOneByOne();
    };

    // get record
    const getRecord = useTask(
      () => checklistDetail(props.id),
      async data => {
        questionsUuids.value = Array.isArray(data.questions) ? [...data.questions] : Object.values(data.questions);

        // @ts-ignore
        const {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          questions,
          ...rest
        } = data;
        record.value = {
          ...rest,
          questions: {},
        };

        await fetchQuestionsOneByOne();
      },
    );
    watch(ref(props.id), getRecord.run);

    return function render() {
      return h('div', [
        h('div', [
          container(
            [
              level(
                [h(BackButton), pageTitle(t('Summary for checklists'))],
              ),
              box(
                [
                  loader('div', getRecord, [
                    record.value ? h(Checklist, {
                      class: 'legacy',
                      props: {
                        record: record.value,
                        questionsToFetch: questionsUuids.value,
                      },
                    }) : null,
                  ], false),
                ],
              ),
            ], 'medium',
          ),
        ]),
      ]);
    };
  },
});

export const summaryChecklistsDetailPageLink = (anchor: VNodeChildren, id: number) => namedLink(
  anchor,
  'analyticsSummaryChecklistsDetail',
  id.toString(),
);
